// Per-domain row in the Grading Pipeline widget. Replaces the original
// nested-ternary status indicator with a small `StatusDot` sub-component.
import React from 'react';
import { CheckCircle2, Clock, Zap, ArrowRight } from 'lucide-react';
import { domainBarColor } from './overviewHelpers';
import { formatETA } from './GradingStatusBreakdownModal';

const StatusDot = ({ isGrading, pct, noTranscription }) => {
  if (isGrading) {
    return (
      <span className="relative flex h-3 w-3">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75" />
        <span className="relative inline-flex rounded-full h-3 w-3 bg-teal-500" />
      </span>
    );
  }
  if (pct === 100) return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
  if (noTranscription) return <Clock className="w-4 h-4 text-white/20" />;
  return <div className="w-3 h-3 rounded-full bg-white/15" />;
};

const ProgressBadges = ({ isGrading, noTranscription, pendingCount, pct }) => (
  <>
    {isGrading && (
      <span className="text-xs px-2 py-0.5 rounded-full bg-teal-500/20 text-teal-300 border border-teal-500/30 flex items-center gap-1">
        <Zap className="w-3 h-3" /> Grading
      </span>
    )}
    {noTranscription && pendingCount > 0 && (
      <span className="text-xs px-2 py-0.5 rounded-full bg-white/5 text-white/25 border border-white/10">
        No transcripts
      </span>
    )}
    {pct === 100 && (
      <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/20">
        Complete
      </span>
    )}
  </>
);

export const DomainProgressRow = ({ d, onClick, onDetailClick }) => {
  // Prefer server-computed fields (new). Fall back to the old client-side
  // math for domains that haven't hit the new endpoint yet.
  const graded = d.graded != null ? d.graded : (d.analyzed || 0) + (d.escalated || 0);
  const gradeableTotal = d.gradeable_total != null
    ? d.gradeable_total
    : graded + (d.pending || 0);
  const historicalPct = d.historical_pct != null
    ? Math.round(d.historical_pct)
    : (gradeableTotal ? Math.round(100 * graded / gradeableTotal) : 0);
  const totalCalls = d.total_calls || 0;
  const ungradeable = (d.skipped_short || 0) + (d.no_transcript || 0);

  const isGrading = (d.gradeable || 0) > 0;
  const noTranscription = (d.pending || 0) > 0 && (d.gradeable || 0) === 0 && graded < totalCalls;

  // Regrade bar values (only meaningful while a regrade is running).
  const regradeCompleted = d.regrade_completed ?? graded;
  const regradeTarget = d.regrade_target ?? gradeableTotal;
  const regradePct = regradeTarget ? Math.round(100 * regradeCompleted / regradeTarget) : 0;

  const barColor = domainBarColor(historicalPct, isGrading);

  return (
    <div
      onClick={onClick}
      className="group flex items-center gap-4 px-5 py-3.5 hover:bg-white/5 cursor-pointer transition-colors rounded-lg -mx-1"
      data-testid={`grading-row-${d.domain_id}`}
    >
      <div className="flex-shrink-0 w-7 flex justify-center">
        <StatusDot isGrading={isGrading} pct={historicalPct} noTranscription={noTranscription} />
      </div>

      <div className="w-40 flex-shrink-0">
        <p className="text-white text-sm font-medium truncate group-hover:text-teal-300 transition-colors">
          {d.company_name || d.yabbit_domain}
        </p>
        <p className="text-white/25 text-xs truncate">{d.yabbit_domain}</p>
      </div>

      <div className="flex-1 min-w-0 space-y-1">
        {/* Historical bar — always shown */}
        <div>
          <div className="flex justify-between text-[10px] text-white/40 mb-0.5">
            <span>Historical (of gradeable)</span>
            <span className="tabular-nums">{graded}/{gradeableTotal} · {historicalPct}%</span>
          </div>
          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-700 ${barColor}`}
              style={{ width: `${Math.max(historicalPct, historicalPct > 0 ? 1 : 0)}%` }}
            />
          </div>
        </div>
        {/* Regrade cycle bar — only while a cycle is in progress */}
        {isGrading && (
          <div>
            <div className="flex justify-between text-[10px] text-teal-300/70 mb-0.5">
              <span>
                Regrade cycle
                {d.regrade_started_at && (
                  <span className="text-white/30 ml-1">
                    · started {new Date(d.regrade_started_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </span>
              <span className="tabular-nums">
                {regradeCompleted}/{regradeTarget} · {regradePct}%
                {d.regrade_eta_sec != null && <span className="text-white/40"> · ~{formatETA(d.regrade_eta_sec)} left</span>}
              </span>
            </div>
            <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full bg-teal-500 animate-pulse transition-all duration-700"
                style={{ width: `${Math.max(regradePct, regradePct > 0 ? 1 : 0)}%` }}
              />
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center gap-3 flex-shrink-0 text-right">
        <div className="text-right hidden sm:block">
          <p className="text-white text-sm font-semibold">
            {graded}<span className="text-white/30 font-normal">/{gradeableTotal}</span>
          </p>
          <p className="text-white/25 text-[10px]">
            {historicalPct}% graded
            {ungradeable > 0 && <span className="text-white/40"> · {ungradeable} un-gradeable</span>}
          </p>
        </div>
        <ProgressBadges
          isGrading={isGrading}
          noTranscription={noTranscription}
          pendingCount={d.pending || 0}
          pct={historicalPct}
        />
        {onDetailClick && (
          <button
            onClick={(e) => { e.stopPropagation(); onDetailClick(); }}
            className="p-1 rounded text-white/25 hover:text-white/70 hover:bg-white/5"
            title="Open full domain detail"
            data-testid={`domain-detail-${d.domain_id}`}
          >
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};
